import React, { useContext, useEffect, useState, useMemo } from "react";
import { Grid, Paper, Stack, Tooltip } from "@mui/material";
import { Alert, Button } from "@platform/design-system";
import { ArrowBack } from "@mui/icons-material";
import { RFP_ACTION_VALUES } from "@platform/rfp-core";

import { RfpContext } from "../../contexts";
import { RFP_ACTION_TYPES } from "../../constants";
import { getPercentageUploadedDocs, sortRFPDocumentTypes } from "../../utils";
import { Title } from "../../components";
import { scrollToTop, mergeFileData } from "../../../../utils";
import DocumentsHelper from "../../coverages/components/documentsHelper";

import SectionSelectType from "./sectionSelectType";
import SectionChecklist from "./sectionChecklist";

const {
  DOCUMENT_TYPES,
  ARE_DOCUMENTS_VALID,
  UPLOADED_FILES,
  DOCUMENTS,
  ADDITIONAL_DOCUMENTS,
} = RFP_ACTION_VALUES;
const { UPDATE_BACK_STEP, UPDATE_NEXT_STEP } = RFP_ACTION_TYPES;

const Documents = ({ px }) => {
  const { rfp, setRfp } = useContext(RfpContext);
  const documentTypes = sortRFPDocumentTypes(rfp[DOCUMENT_TYPES]);
  const areDocumentsValid = rfp[ARE_DOCUMENTS_VALID];
  const uploadedFiles = rfp[UPLOADED_FILES];
  const additionalDocuments = rfp[ADDITIONAL_DOCUMENTS];
  const documents = rfp[DOCUMENTS];
  const [additionalFiles, setAdditionalFiles] = useState([]);

  useEffect(() => {
    const processAdditionalDocuments = () => {
      const additionalDocumentsList = Array.isArray(additionalDocuments)
        ? additionalDocuments
        : [];
      const additionalDocumentsFiles = [];
      additionalDocumentsList.forEach((ad) => {
        const document = documents.files?.find((fileItem) => {
          if (fileItem.filename && !fileItem.name) {
            fileItem["name"] = fileItem.filename;
          }
          const fileName = fileItem.name || fileItem.filename;
          return fileName
            ? fileName === ad || fileName.replace(/\+/g, " ") === ad
            : false;
        });
        if (document) {
          additionalDocumentsFiles.push(document);
        }
      });
      setAdditionalFiles(additionalDocumentsFiles);
    };

    processAdditionalDocuments();
  }, [documents, additionalDocuments]);

  const progressValue = useMemo(
    () => getPercentageUploadedDocs(documentTypes, "required"),
    [documentTypes],
  );

  const handleBack = () => {
    setRfp({ type: UPDATE_BACK_STEP });
    scrollToTop();
  };

  const handleNext = () => {
    setRfp({ type: UPDATE_NEXT_STEP });
    scrollToTop();
  };

  return (
    <Grid container display="block" px={px}>
      <Title title="Upload Documents" />
      <DocumentsHelper documentTypes={documentTypes} />
      {progressValue === 100 ? (
        <Alert
          severity="success"
          title="All required Documents are complete"
          sx={{ mb: 2 }}
        />
      ) : null}
      <Grid container size={12} component={Paper} sx={{ padding: 3 }}>
        <SectionSelectType
          documentTypes={documentTypes}
          uploadedFiles={uploadedFiles}
          additionalFiles={additionalFiles}
        />
        <SectionChecklist
          documentTypes={documentTypes}
          progressValue={progressValue}
          uploadedFiles={mergeFileData(uploadedFiles, additionalFiles)}
        />
      </Grid>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        mt={2}
      >
        <Button
          variant="tertiary"
          onClick={handleBack}
          startIcon={<ArrowBack />}
        >
          Back
        </Button>
        <Stack direction="row" gap={1} alignItems="center">
          <Tooltip
            placement="top"
            disableHoverListener={areDocumentsValid}
            title={
              "The next step is not available until all required documents are uploaded and all documents have coverage assignments"
            }
          >
            <span>
              <Button
                variant="primary"
                onClick={handleNext}
                disabled={!areDocumentsValid}
              >
                Next: Review
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Grid>
  );
};

export default Documents;
